import { Kind, OperationTypeNode } from 'graphql'
import type {
  SelectionNode,
  VariableDefinitionNode,
  DocumentNode,
  FieldNode,
  OperationDefinitionNode,
  ArgumentNode,
  ValueNode,
  DefinitionNode
} from 'graphql'

export interface MutationOperationAndVariables {
  mutation: DocumentNode
  variables: any
}

export interface MergeResult {
  document: DocumentNode
  variableNameMaps: VariableNameMap[]
}

export interface VariableNameMap {
  [key: string]: string
}

// A helper function to replace a variable name in the value node with the new name from the map.
export function replaceVariableNameInValueNode(
  valueNode: ValueNode,
  variableNameMap: VariableNameMap
): ValueNode {
  if (valueNode.kind === Kind.VARIABLE) {
    const newVariableName = variableNameMap[valueNode.name.value]
    return { ...valueNode, name: { kind: Kind.NAME, value: newVariableName } }
  } else if (valueNode.kind === Kind.OBJECT) {
    return {
      ...valueNode,
      fields: valueNode.fields.map((field) => ({
        ...field,
        value: replaceVariableNameInValueNode(field.value, variableNameMap)
      }))
    }
  } else if (valueNode.kind === Kind.BOOLEAN) {
    return valueNode
  }
  throw new Error('The mutation query coming here is not expected.')
}

// Replaces variable names in the arguments with new names from the map.
function replaceVariableNamesInArguments(
  args: ReadonlyArray<ArgumentNode>,
  variableNameMap: VariableNameMap
): ArgumentNode[] {
  return args.map((arg) => ({
    ...arg,
    value: replaceVariableNameInValueNode(arg.value, variableNameMap)
  }))
}

// Creates a map of old variable names to new ones and a new list of variable definitions.
function createVariableMapAndVariableDefs(
  variableDefinitions: ReadonlyArray<VariableDefinitionNode>,
  variableName: string
): { variableNameMap: VariableNameMap; newVariableDefs: ReadonlyArray<VariableDefinitionNode> } {
  const variableNameMap: VariableNameMap = {}
  const newVariableDefs = variableDefinitions.map((vdef, index) => {
    const originalName = vdef.variable.name.value
    const newName = `${variableName}_${index + 1}`
    variableNameMap[originalName] = newName
    return {
      ...vdef,
      variable: { ...vdef.variable, name: { ...vdef.variable.name, value: newName } }
    }
  })
  return { variableNameMap, newVariableDefs }
}

// Modifies operation definition node by changing alias name and variable names.
export function modifyOperationDefinitionNode(
  operationDefinition: OperationDefinitionNode,
  aliasName: string,
  variableName: string
): { operationDefinition: OperationDefinitionNode; variableNameMap: VariableNameMap } {
  const { variableNameMap, newVariableDefs } = createVariableMapAndVariableDefs(
    operationDefinition.variableDefinitions!,
    variableName
  )

  const newSelections = operationDefinition.selectionSet.selections.map(
    (selection: SelectionNode) => {
      if (selection.kind === Kind.FIELD) {
        return {
          ...selection,
          alias: { kind: Kind.NAME, value: aliasName },
          arguments: selection.arguments
            ? replaceVariableNamesInArguments(selection.arguments, variableNameMap)
            : [],
          selectionSet: selection.selectionSet
        } as FieldNode
      }
      throw new Error('The mutation query coming here is not expected.')
    }
  )

  return {
    operationDefinition: {
      ...operationDefinition,
      variableDefinitions: newVariableDefs,
      selectionSet: { ...operationDefinition.selectionSet, selections: newSelections }
    },
    variableNameMap
  }
}

// Modifies document by changing alias name and variable names.
export function modifyDocumentByChangingAliasAndVariableNames(
  document: DocumentNode,
  aliasName: string,
  variableName: string
): { document: DocumentNode; variableNameMap: VariableNameMap } {
  let variableNameMap: VariableNameMap = {}

  const newDefinitions = document.definitions.map((definition: DefinitionNode) => {
    if (
      definition.kind === Kind.OPERATION_DEFINITION &&
      'selectionSet' in definition &&
      'variableDefinitions' in definition
    ) {
      const result = modifyOperationDefinitionNode(definition, aliasName, variableName)
      variableNameMap = result.variableNameMap
      return result.operationDefinition
    }
    throw new Error('The mutation query coming here is not expected.')
  })

  return { document: { ...document, definitions: newDefinitions }, variableNameMap }
}

// Get variable definitions from the operation definition node in the document.
export function getVariableDefinitionsFromDocument(
  document: DocumentNode
): VariableDefinitionNode[] | undefined {
  const operationDefinition = document.definitions.find(
    (def) => def.kind === Kind.OPERATION_DEFINITION
  )
  if (!operationDefinition || operationDefinition.kind !== Kind.OPERATION_DEFINITION) {
    throw new Error('Expect the OperationDefinition and nothing else.')
  }
  return operationDefinition.variableDefinitions
    ? [...operationDefinition.variableDefinitions]
    : undefined
}

// Get selections from the operation definition node in the document.
export function getSelectionsFromDocument(document: DocumentNode): SelectionNode[] | undefined {
  const operationDefinition = document.definitions.find(
    (def) => def.kind === Kind.OPERATION_DEFINITION
  )
  if (!operationDefinition || operationDefinition.kind !== Kind.OPERATION_DEFINITION) {
    throw new Error('Expect the OperationDefinition and nothing else.')
  }
  return [...operationDefinition.selectionSet.selections]
}

function ensureMutationOperation(doc: DocumentNode): void {
  const operationDefinition = doc.definitions.find(
    (def) => def.kind === Kind.OPERATION_DEFINITION
  ) as OperationDefinitionNode | undefined

  if (
    operationDefinition === undefined ||
    operationDefinition.operation !== OperationTypeNode.MUTATION
  ) {
    throw new Error('Not mutation query')
  }
}

// Merge multiple mutation documents into a single mutation document.
export function mergeMutationDocuments(documents: DocumentNode[]): {
  document: DocumentNode
  variableNameMaps: VariableNameMap[]
} {
  let combinedVariableDefinitions: VariableDefinitionNode[] = []
  let combinedSelections: SelectionNode[] = []
  const combinedVariableNameMaps: VariableNameMap[] = []

  documents.forEach((doc, index) => {
    ensureMutationOperation(doc)

    const newVariableName = `input_${index + 1}`
    const newAliasName = `query_${index + 1}`

    const { document: modifiedDocument, variableNameMap } =
      modifyDocumentByChangingAliasAndVariableNames(doc, newAliasName, newVariableName)

    combinedVariableNameMaps.push(variableNameMap)

    const modifiedVariableDefinitions = getVariableDefinitionsFromDocument(modifiedDocument)
    const modifiedSelections = getSelectionsFromDocument(modifiedDocument)

    if (modifiedVariableDefinitions) {
      combinedVariableDefinitions = [...combinedVariableDefinitions, ...modifiedVariableDefinitions]
    }
    if (modifiedSelections) {
      combinedSelections = [...combinedSelections, ...modifiedSelections]
    }
  })

  return {
    document: {
      kind: Kind.DOCUMENT,
      definitions: [
        {
          kind: Kind.OPERATION_DEFINITION,
          operation: OperationTypeNode.MUTATION,
          name: { kind: Kind.NAME, value: 'combinedMutation' },
          variableDefinitions: combinedVariableDefinitions,
          selectionSet: { kind: Kind.SELECTION_SET, selections: combinedSelections }
        }
      ]
    },
    variableNameMaps: combinedVariableNameMaps
  }
}

export function mergeMutationVariables(variables: any[], variableNameMaps: VariableNameMap[]): any {
  // Ensure the input arrays have the same length
  if (variables.length !== variableNameMaps.length) {
    throw new Error('Input arrays must have the same length')
  }
  const mergedVariables: { [key: string]: any } = {}

  for (let i = 0; i < variables.length; i++) {
    const variableMap = variableNameMaps[i]
    const variable = variables[i]

    // Ensure the keys of variables and variableNameMaps are the same
    const variableKeys = Object.keys(variable)
    const variableMapKeys = Object.keys(variableMap)
    if (JSON.stringify(variableKeys.sort()) !== JSON.stringify(variableMapKeys.sort())) {
      throw new Error(`Key mismatch in variables and variableNameMaps at index ${i}`)
    }

    for (const key in variable) {
      const newKey = variableMap[key]
      mergedVariables[newKey] = variable[key]
    }
  }

  return mergedVariables
}
